import React, { useState, useEffect, useRef } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { usePolling } from "@/hooks/usePolling";
import { cn } from "@/lib/utils";
import { LayoutDashboard, PlusCircle, Users, MapPin, UserCog, LogOut, CalendarClock, Bell, AlertOctagon, X, Settings, Fuel, PackageSearch, Bot, FileText, Upload, KeyRound, Menu, Compass } from "lucide-react";
import { ROL_LABEL, INCIDENTE_TIPO } from "@/lib/constants";
import { Logo } from "@/components/marca/Logo";
import { Iniciales } from "@/components/marca/Layout";
import { ContainerId } from "@/components/marca/ContainerId";
import { useGuia } from "@/components/guia/Guia";
import { IdiomaSelector } from "@/components/IdiomaSelector";
import { t } from "@/lib/i18n"; // Prompt M

function beep() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    const ctx = new Ctx();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.connect(g); g.connect(ctx.destination);
    o.type = "square"; o.frequency.value = 880;
    g.gain.setValueAtTime(0.15, ctx.currentTime);
    o.start();
    o.stop(ctx.currentTime + 0.25);
  } catch { /* navegador no permite */ }
}

export default function PanelLayout() {
  const { user, logout } = useAuth();
  const { iniciar: iniciarGuia } = useGuia(); // Prompt M
  const navigate = useNavigate();
  const [alertCount, setAlertCount] = useState(0);
  const [banner, setBanner] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const seenRef = useRef(null);

  const esMantenimiento = user?.rol === "mantenimiento";

  const pollAlertas = async () => {
    if (esMantenimiento) return;
    try {
      const { data } = await api.get("/alertas");
      setAlertCount(data.total);
      const ids = new Set((data.incidentes_abiertos || []).map((i) => i.id));
      if (seenRef.current !== null) {
        const nuevos = (data.incidentes_abiertos || []).filter((i) => !seenRef.current.has(i.id));
        if (nuevos.length > 0) { setBanner(nuevos[0]); beep(); }
      }
      seenRef.current = ids;
    } catch { /* */ }
  };

  usePolling(pollAlertas, 15000);

  const grupos = esMantenimiento
    ? [{ g: "REGISTROS", items: [{ to: "/panel/insumos", label: t("Insumos"), icon: PackageSearch, end: true, testid: "nav-insumos" }] }]
    : [
      { g: "OPERACIÓN", items: [
        { to: "/panel", label: t("Panel del día"), icon: LayoutDashboard, end: true, testid: "nav-panel" },
        { to: "/panel/cargas/nueva", label: t("Nueva carga"), icon: PlusCircle, testid: "nav-nueva-carga" },
        { to: "/panel/alertas", label: t("Alertas"), icon: Bell, testid: "nav-alertas", badge: true },
        { to: "/panel/jornadas", label: t("Jornadas"), icon: CalendarClock, testid: "nav-jornadas" },
      ] },
      { g: "REGISTROS", items: [
        { to: "/panel/diesel", label: t("Diésel"), icon: Fuel, testid: "nav-diesel" },
        { to: "/panel/insumos", label: t("Insumos"), icon: PackageSearch, testid: "nav-insumos" },
      ] },
      { g: "HERRAMIENTAS", items: [
        { to: "/panel/agente", label: t("Agente"), icon: Bot, testid: "nav-agente" },
        { to: "/panel/reportes", label: t("Reportes"), icon: FileText, testid: "nav-reportes" },
      ] },
      { g: "CATÁLOGOS", items: [
        { to: "/panel/clientes", label: t("Clientes"), icon: Users, testid: "nav-clientes" },
        { to: "/panel/ubicaciones", label: t("Ubicaciones"), icon: MapPin, testid: "nav-ubicaciones" },
      ] },
      ...(user?.rol === "admin" ? [{ g: "ADMINISTRACIÓN", items: [
        { to: "/panel/usuarios", label: t("Usuarios"), icon: UserCog, testid: "nav-usuarios" },
        { to: "/panel/importar", label: t("Importar"), icon: Upload, testid: "nav-importar" },
        { to: "/panel/configuracion", label: t("Configuración"), icon: Settings, testid: "nav-configuracion" },
      ] }] : []),
    ];

  return (
    <div className="min-h-screen flex bg-paper">
      <header className="lg:hidden fixed top-0 inset-x-0 z-30 bg-navy text-white flex items-center justify-between px-4 h-14">
        <button data-testid="panel-mobile-menu-btn" onClick={() => setMobileOpen(true)} className="p-1 -ml-1"><Menu className="h-6 w-6" /></button>
        <Logo size={28} light className="gap-2" />
        <IdiomaSelector claro className="scale-90" />
        <button data-testid="guia-abrir-movil" aria-label={t("Guía de la plataforma")} onClick={() => iniciarGuia()} className="p-1 -mr-1"><Compass className="h-6 w-6" /></button>
      </header>

      {mobileOpen && (
        <div data-testid="panel-mobile-backdrop" className="lg:hidden fixed inset-0 z-40 bg-navy/60" onClick={() => setMobileOpen(false)} />
      )}

      <aside className={cn(
        "w-[236px] bg-navy text-[#D3DCE8] flex flex-col fixed inset-y-0 left-0 z-50 transition-transform duration-200",
        mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
      )}>
        <div className="flex items-center gap-2 px-5 py-5">
          <Logo size={34} light eyebrow={t("PANEL DE OPERACIÓN")} className="flex-1" />
          <button data-testid="panel-mobile-close-btn" onClick={() => setMobileOpen(false)} className="lg:hidden p-1 text-[#9FB1C8]"><X className="h-5 w-5" /></button>
        </div>
        <div className="px-5 pb-1 hidden lg:block"><IdiomaSelector claro /></div>
        <nav className="flex-1 px-3 pb-3 overflow-y-auto">
          {grupos.map((gr) => (
            <div key={gr.g} className="mt-4 first:mt-1">
              <p className="eyebrow px-3 mb-1" style={{ color: "#9FB1C8", fontSize: 10.5 }}>{t(gr.g)}</p>
              <div className="space-y-0.5">
                {gr.items.map((it) => (
                  <NavLink
                    key={it.to}
                    to={it.to}
                    end={it.end}
                    data-testid={it.testid}
                    onClick={() => setMobileOpen(false)}
                    className={({ isActive }) =>
                      cn(
                        "relative flex items-center gap-3 pl-3 pr-3 py-2 rounded-md text-[14px] font-medium transition-colors duration-150",
                        isActive ? "bg-brand text-white" : "hover:bg-[#2D4C73] hover:text-white" // Fix N (2026-10-13): sección activa en rojo #C93646
                      )
                    }
                  >
                    {({ isActive }) => (<>
                      <it.icon className="h-[17px] w-[17px]" /> <span className="flex-1">{it.label}</span>
                      {it.badge && alertCount > 0 && (
                        <span data-testid="alertas-badge" className="min-w-[20px] h-5 px-1.5 rounded-full bg-gold text-navy text-xs font-bold flex items-center justify-center">{alertCount}</span>
                      )}
                    </>)}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>
        <div className="p-3 border-t border-[#35557B] space-y-0.5">
          <div className="px-2 py-2 flex items-center gap-2.5">
            <Iniciales nombre={user?.nombre} size={32} className="!bg-[#35557B] !text-white" />
            <div className="min-w-0">
              <p className="text-[14px] font-semibold text-white truncate">{user?.nombre}</p>
              <p className="text-[12.5px] text-[#9FB1C8]">{ROL_LABEL[user?.rol]}</p>
            </div>
          </div>
          <button data-testid="guia-abrir" onClick={() => { setMobileOpen(false); setTimeout(() => iniciarGuia(), 250); }} className="flex items-center gap-3 w-full px-3 py-2 rounded-md text-[14px] hover:bg-[#2D4C73] hover:text-white">
            <Compass className="h-[17px] w-[17px]" />{t(" Guía de la plataforma")}</button>
          <button data-testid="panel-cambiar-password" onClick={() => { setMobileOpen(false); navigate("/cambiar-password"); }} className="flex items-center gap-3 w-full px-3 py-2 rounded-md text-[14px] hover:bg-[#2D4C73] hover:text-white">
            <KeyRound className="h-[17px] w-[17px]" />{t(" Cambiar contraseña")}</button>
          <button data-testid="panel-logout" onClick={() => { logout(); navigate("/login"); }} className="flex items-center gap-3 w-full px-3 py-2 rounded-md text-[14px] hover:bg-[#2D4C73] hover:text-white">
            <LogOut className="h-[17px] w-[17px]" />{t(" Cerrar sesión")}</button>
        </div>
      </aside>
      <main className="flex-1 min-w-0 pt-14 lg:pt-0 lg:ml-[236px]">
        {banner && (
          <div data-testid="alerta-banner" className="sticky top-14 lg:top-0 z-20 bg-danger text-white px-4 sm:px-6 py-3 flex items-center justify-between gap-2">
            <button className="flex items-center gap-2 font-semibold text-left text-sm" onClick={() => { if (banner.carga_id) navigate(`/panel/cargas/${banner.carga_id}`); else navigate("/panel/alertas"); setBanner(null); }}>
              <AlertOctagon className="h-5 w-5 shrink-0" /> <span className="font-semibold">{t("Nuevo problema: {v0}{v1}", { v0: INCIDENTE_TIPO[banner.tipo] || banner.tipo, v1: banner.chofer_nombre ? ` — ${banner.chofer_nombre}` : "" })}</span>{banner.contenedor ? <> (<ContainerId numero={banner.contenedor} size="sm" className="inline-flex" />)</> : ""}
            </button>
            <button onClick={() => setBanner(null)} className="p-1 shrink-0"><X className="h-5 w-5" /></button>
          </div>
        )}
        <Outlet />
      </main>
    </div>
  );
}
